import type { PillColor } from "components/PersonPill";
import "components/PersonPill/index.css";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import { ButtonHTMLAttributes, forwardRef } from "react";
import { useReference } from "./hooks";
import type {
  ActivityAction,
  ActivityRow,
  ActivityStatus,
  ApprovalItem,
} from "./types";
import { ACTION_PILL_WORDS, reasonWords, STATUS_LABELS } from "./words";

/*
 * Activity's pills, in the soft pill colors people and chips already use:
 * a row's Status (with the reason on hover when it failed or was skipped),
 * an item's Waiting, and the Action, by the kind of step.
 */

const STATUS_COLORS: Record<ActivityStatus, PillColor> = {
  completed: "green",
  failed: "red",
  skipped: "gray",
};

/**
 * Each kind of step's tint, as Done's pills had them: messages blue, tasks
 * green, moves purple, field updates gold. Closing a task is a task's
 * green, assigning a tutor the tutors' gold, and a note a message's blue.
 */
const ACTION_COLORS: Record<ActivityAction, PillColor> = {
  message: "blue",
  task: "green",
  crm_move: "purple",
  crm_field: "gold",
  close_task: "green",
  assign_tutor: "gold",
  add_note: "blue",
  wait: "gray",
};

/** How a row ended: Completed, Failed, or Skipped (Not now, Cleared and Stopped are Skipped too). */
export function statusOf(row: ActivityRow): ActivityStatus {
  if (row.pill === "failed") return "failed";
  if (row.pill === "skipped") return "skipped";
  return "completed";
}

/** A pill whose reason shows on hover, and from the keyboard. */
function WithReason({
  pill,
  label,
  reason,
}: {
  pill: string;
  label: string;
  reason: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger>
        <span tabIndex={0} className={`${pill} cursor-default`}>
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <span className="block max-w-xs text-sm leading-5">{reason}</span>
      </TooltipContent>
    </Tooltip>
  );
}

/** A row's Status: Completed green, Failed red, Skipped gray; hovering Failed or Skipped says why. */
export function StatusPill({ row }: { row: ActivityRow }) {
  const ref = useReference();
  const status = statusOf(row);
  const pill = `person-pill person-pill--${STATUS_COLORS[status]}`;
  const label = STATUS_LABELS[status];
  if (status === "completed") return <span className={pill}>{label}</span>;
  return (
    <WithReason pill={pill} label={label} reason={reasonWords(ref, row)} />
  );
}

/** An item's Status in Waiting for approval: Waiting, orange as its count; hovering says why it waits besides Ask first. */
export function WaitingPill({ item }: { item: ApprovalItem }) {
  const pill = "person-pill person-pill--orange";
  if (!item.note) return <span className={pill}>Waiting</span>;
  return <WithReason pill={pill} label="Waiting" reason={item.note} />;
}

/**
 * An action in words, as its Action pill says it: a message is always
 * "Message"; anything else says what it did once done ("Task created"),
 * else only its kind ("Task"), as one skipped, failed or still waiting did
 * not do it.
 */
export function actionWords(action: ActivityAction, done: boolean) {
  const words = ACTION_PILL_WORDS[action];
  if (action === "message") return words.kind;
  return done ? words.done : words.kind;
}

/** A Done row's Action in words: "Task created" once done, else "Task"; a message's is "Message". */
export function actionLabel(row: ActivityRow) {
  return actionWords(row.action, statusOf(row) === "completed");
}

/** An Action, tinted by its kind of step, as a pill button (what it did, or would do, opens from it). */
export const ActionPill = forwardRef<
  HTMLButtonElement,
  {
    action: ActivityAction;
    label: string;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function ActionPill({ action, label, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={`person-pill person-pill--${ACTION_COLORS[action]} person-pill--button`}
      {...props}
    >
      {label}
    </button>
  );
});
